import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Shield } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { type DevRole, DEV_ROLES, getDevRoleOverride, setDevRoleOverride } from '../../lib/devRoleOverride';

const ROLE_VARIANT: Record<DevRole, 'default' | 'secondary' | 'destructive' | 'warning'> = {
  Administrator: 'default',
  Approver: 'secondary',
  ReadOnly: 'warning',
  NoAccess: 'destructive',
};

export function DevRoleSwitcher() {
  const queryClient = useQueryClient();
  const [currentRole, setCurrentRole] = useState<DevRole | null>(
    () => (getDevRoleOverride() as DevRole) ?? null,
  );

  const handleRoleChange = useCallback(
    (value: string) => {
      const role = value === 'none' ? null : (value as DevRole);
      setDevRoleOverride(role);
      setCurrentRole(role);
      queryClient.invalidateQueries();
    },
    [queryClient],
  );

  return (
    <div className="flex items-center gap-2 rounded-md border border-dashed border-amber-400 bg-amber-50 px-2 py-1">
      <Shield className="h-3.5 w-3.5 text-amber-600" />
      <Select value={currentRole ?? 'none'} onValueChange={handleRoleChange}>
        <SelectTrigger size="sm" className="h-7 w-[140px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">
            <span className="text-muted-foreground">Default</span>
          </SelectItem>
          {DEV_ROLES.map((role) => (
            <SelectItem key={role} value={role}>
              <Badge variant={ROLE_VARIANT[role]} className="text-[10px] px-1.5 py-0">
                {role}
              </Badge>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
